/**
 * Single source of truth for the for-professionals page content.
 * Used by ProfessionalsDeckSpView.vue (runtime) and scripts/inject-route-meta.js
 * (build-time prerender for bots that don't run JS), so prices and features
 * can never drift between the page, the bot HTML and the JSON-LD.
 * CommonJS on purpose: the build script runs in plain Node.
 */
module.exports = {
  platformChips: [
    { t: "Historia clínica", s: "ficha completa y cifrada" },
    { t: "Planes de tratamiento", s: "el plan del paciente, en un clic" },
    { t: "Firma digital", s: "consentimientos con versionado" },
    { t: "Agenda inteligente", s: "en tiempo real: salas, equipos y aforos" },
    { t: "Reservas online 24/7", s: "Portal, WhatsApp y Medkus.com" },
    { t: "Comunicación", s: "WhatsApp, portal, email y SMS" },
    { t: "Seguimiento del paciente", s: "continuidad y alertas de abandono" },
    { t: "Facturación automática", s: "cobros, gastos, depósitos y Verifactu" },
    { t: "Captación y marketing", s: "pacientes nuevos" },
    { t: "Asistente de IA", s: "lee analíticas y rellena tu agenda" },
    { t: "Membresías", s: "ingresos recurrentes" },
    { t: "Programas terapéuticos", s: "diséñalos y véndelos online" },
    { t: "Sesiones grupales y clases", s: "talleres y grupos" },
    { t: "Presencia y reputación", s: "perfil y reseñas" },
    { t: "Formación", s: "vende tus cursos y certificaciones" },
    { t: "Analítica y dashboard", s: "decisiones con datos" },
    { t: "Multicentro", s: "varias sedes, una plataforma" },
    { t: "TPV y tienda", s: "productos y ventas" },
  ],

  featureSections: [
    {
      id: "crecer",
      q: "¿Cómo llenas tu agenda sin depender solo del boca a boca ni gastar en marketing?",
      a: "Medkus no solo gestiona tu consulta: trabaja para llenarla.",
      cards: [
        {
          h: "Captación y marketing",
          d: "Atrae y recupera pacientes, sin agencias.",
          pills: [
            "Marketplace de Medkus.com",
            "Campañas de marketing",
            "Reactivación de pacientes",
            "IA que rellena huecos",
          ],
        },
        {
          h: "Presencia y reputación",
          d: "Conviértete en el referente de tu zona.",
          pills: [
            "Perfil en Medkus.com",
            "Artículos y posts",
            "Reseñas de pacientes",
            "Estadísticas y predicción",
          ],
        },
      ],
    },
    {
      id: "cuidar",
      q: "¿Cómo cuidas a un paciente dentro y fuera de la consulta?",
      a: "Con toda su historia y todo su seguimiento en un mismo lugar.",
      cards: [
        {
          h: "Historia clínica y planes",
          d: "Su ficha completa, y su plan en segundos.",
          pills: [
            "Historial y evolución",
            "Planes en segundos",
            "Compartir en un clic",
            "La IA lee sus analíticas",
            "Consentimientos digitales",
          ],
        },
        {
          h: "Comunicación y seguimiento",
          d: "Para que nunca se sienta olvidado.",
          pills: [
            "WhatsApp y portal",
            "Email y SMS",
            "Recordatorios automáticos",
            "Continuidad entre visitas",
            "Alertas de riesgo de abandono",
          ],
        },
      ],
    },
    {
      id: "tiempo",
      q: "¿Por qué desaparecen tantas horas cada semana?",
      a: "Porque la agenda, el cobro y el papeleo los hace una persona. Aquí se hacen solos.",
      cards: [
        {
          h: "Agenda inteligente",
          d: "Di adiós a los WhatsApps de confirmación.",
          pills: [
            "Portal del paciente",
            "WhatsApp automático",
            "Medkus.com",
            "Recordatorios",
            "Lista de espera",
            "Relleno de huecos",
            "Depósitos y señales",
          ],
        },
        {
          h: "Facturación automática",
          d: "Conforme a la normativa fiscal española.",
          pills: [
            "Facturas y presupuestos",
            "Pasarela de pago",
            "Cobros recurrentes",
            "Gastos y caja",
            "Exportación contable",
            "Verifactu",
          ],
        },
        {
          h: "Planes de tratamiento",
          d: "Monta el plan y envíalo en un par de clics.",
          pills: [
            "Plan en minutos",
            "PDF con tu marca",
            "Envío en un clic",
            "Lo recibe el paciente",
          ],
        },
      ],
    },
    {
      id: "ingresos",
      q: "¿Cómo ganas más sin trabajar más horas?",
      a: "Convirtiendo tu conocimiento en productos que se venden solos.",
      cards: [
        {
          h: "Membresías",
          d: "Ingresos recurrentes.",
          pills: [
            "Niveles de acceso",
            "Contenido exclusivo",
            "Renovación automática",
          ],
        },
        {
          h: "Programas terapéuticos",
          d: "Diseña tu programa y véndelo online.",
          pills: [
            "Por sesiones o fases",
            "Pago online",
            "Seguimiento automático",
            "Alertas de abandono",
          ],
        },
        {
          h: "Sesiones grupales y clases",
          d: "Talleres, retiros y grupos.",
          pills: ["Inscripción online", "Aforos y asistencia", "Bonos y packs"],
        },
      ],
    },
    {
      id: "seguridad",
      q: "¿Y los datos de tus pacientes?",
      a: "Tratados como lo que son: datos de salud.",
      cards: [
        {
          h: "Protección de datos",
          d: "RGPD sanitario de verdad, no una casilla.",
          pills: [
            "RGPD y LOPDGDD",
            "Datos clínicos cifrados",
            "Permisos por rol",
            "Registro de auditoría",
            "ISO 27001 en proceso",
          ],
        },
        {
          h: "Infraestructura",
          d: "Tus datos, en Europa y por duplicado.",
          pills: [
            "Servidores OVH (UE)",
            "Réplica en tiempo real",
            "Backups en España",
            "Exportación completa",
            "Sin permanencia",
          ],
        },
      ],
    },
  ],

  formacionPills: [
    "Másters para el público",
    "Certificaciones",
    "Venta e inscripción online",
    "Pago único o por fases",
    "También para tu equipo",
  ],

  nicheTags: [
    "Medicina integrativa",
    "Medicina funcional",
    "Antiaging",
    "Longevity",
    "Recovery",
    "Biohacking",
    "PNI y epigenética",
    "Salud hormonal",
    "Salud de la mujer",
    "Nutrición",
    "Bienestar",
  ],

  pricingHeadline: "Precios cerrados, sin permanencia. Y el cambio lo hacemos nosotros.",
  tiers: [
    {
      lab: "En solitario",
      price: "50 €",
      amount: 50,
      unit: "/ mes",
      who: "Profesionales del bienestar no sanitarios que trabajan por su cuenta. Incluye 10 € de IA al mes.",
    },
    {
      lab: "Consulta sanitaria",
      price: "80 €",
      amount: 80,
      unit: "/ mes",
      who: "Médicos y sanitarios en solitario, con historia clínica completa y Verifactu. Incluye 10 € de IA al mes.",
    },
    {
      lab: "Clínica 1-5",
      price: "150 €",
      amount: 150,
      unit: "/ mes",
      who: "Hasta 5 profesionales con agenda. Incluye 20 € de IA al mes.",
    },
    {
      lab: "Clínica 6-10",
      price: "250 €",
      amount: 250,
      unit: "/ mes",
      who: "Hasta 10 profesionales con agenda. Incluye 30 € de IA al mes.",
    },
    {
      lab: "Clínica 11-15",
      price: "350 €",
      amount: 350,
      unit: "/ mes",
      who: "Hasta 15 profesionales con agenda. Incluye 40 € de IA al mes.",
    },
    {
      lab: "Multicentro",
      price: "a convenir",
      amount: null,
      unit: "",
      who: "Más de 15 profesionales o varias sedes: precio por sede con descuento.",
    },
  ],
  pricingNote:
    "Cuentan solo los profesionales con agenda; recepción y administración no suman. La IA incluida es un crédito mensual: si un mes se supera, el exceso se factura a la tarifa publicada, sin sorpresas.",
};
